import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Upload,
} from 'antd'
import {
  QuestionCircleOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Progress } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import MDEditor from '@uiw/react-md-editor'
import { getCommands } from '@uiw/react-md-editor/commands'
import dayjs from 'dayjs'
import '@uiw/react-md-editor/markdown-editor.css'
import { AgreementTemplateType } from '../services/agreementService'

// 导入接口相关的 hooks 和类型
import {
  useCourses,
  useCreateCourse,
  useDeleteCourse,
  useUpdateCourse,
} from '../hooks/useCourses'
import { useBatches } from '../hooks/useBatches'
import { useAgreementTemplates } from '../hooks/useAgreementTemplates'
import { useReactQuillImageUpload } from '../hooks/useReactQuillImageUpload'
import { useModalScrollFix } from '../hooks/useBodyScrollLock'
import { getPagination } from '../components/TablePagination'
import type { UploadProps } from 'antd'
import type { ColumnType } from 'antd/es/table/interface'
import type { CourseData, CourseQueryParams } from '../services/courseService'

const uploadProps: UploadProps = {
  name: 'file',
  listType: 'picture-card',
  beforeUpload: () => {
    // 阻止默认上传行为，我们会手动处理
    return false
  },
  onPreview: (file) => {
    // 预览功能
    if (file.url) {
      window.open(file.url)
    }
  },
  maxCount: 1,
  accept: 'image/*',
}

export const Route = createFileRoute('/course')({
  component: CourseManagement,
})

function CourseManagement() {
  // 页面状态
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentCourse, setCurrentCourse] = useState<CourseData | null>(null)
  const [fileList, setFileList] = useState<Array<any>>([])
  const [form] = Form.useForm()

  // 显示历史课程状态 - 默认不显示
  const [showHistoryCourses, setShowHistoryCourses] = useState<boolean>(() => {
    const search = new URLSearchParams(window.location.search)
    return search.get('showHistory') === 'true'
  })

  // Body scroll lock hook
  const { restoreBodyScroll } = useModalScrollFix()

  // 查询参数 - 使用class management的URLSearchParams方式
  const [queryParams, setQueryParams] = useState<CourseQueryParams>(() => {
    const search = new URLSearchParams(window.location.search)
    return {
      pageNum: parseInt(search.get('pageNum') || '1', 10),
      pageSize: parseInt(search.get('pageSize') || '10', 10),
      name: search.get('name') || '',
      batchId: search.get('batchId')
        ? parseInt(search.get('batchId')!, 10)
        : undefined,
    }
  })

  // 同步URL参数变化 - 使用class management的useEffect方式
  useEffect(() => {
    const params = new URLSearchParams()
    if (queryParams.name) params.set('name', queryParams.name)
    if (queryParams.batchId)
      params.set('batchId', queryParams.batchId.toString())
    if (queryParams.pageNum)
      params.set('pageNum', queryParams.pageNum.toString())
    if (queryParams.pageSize)
      params.set('pageSize', queryParams.pageSize.toString())
    if (showHistoryCourses) params.set('showHistory', 'true')
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [queryParams, showHistoryCourses])

  // 动态查询参数 - 根据 showHistoryCourses 决定是否添加 afterEndTime
  const apiQueryParams = useMemo(() => {
    const params = { ...queryParams }
    if (!showHistoryCourses) {
      // 默认只显示未截止的课程
      params.afterEndTime = dayjs().format('YYYY-MM-DDTHH:mm:ss')
    }
    return params
  }, [queryParams, showHistoryCourses])

  // 接口调用
  const { data: coursesData, isLoading } = useCourses(apiQueryParams)
  const { data: batches } = useBatches()
  const agreementTemplates = useAgreementTemplates({
    pageNum: '1',
    pageSize: '1000',
  }).data?.records
  const createCourseMutation = useCreateCourse((courseName: string) => {
    // 创建成功后，更新搜索参数以显示新创建的课程
    setQueryParams((prev) => ({
      ...prev,
      name: courseName,
      pageNum: 1, // 回到第一页
    }))
  })
  const updateCourseMutation = useUpdateCourse()
  const deleteCourseMutation = useDeleteCourse()
  const quillImageUploadMutation = useReactQuillImageUpload()

  // 获取课程数据
  const courses = coursesData?.records || []
  const totalCourses = coursesData?.total || 0

  // 处理表格变化的函数 - 类似class management的handleTableChange
  const handleTableChange = (pagination: any, filters: any) => {
    const nameFilter = filters?.name?.[0]
    const batchIdFilter = filters.batchName?.length
      ? Number(filters.batchName[0])
      : undefined

    setQueryParams({
      pageNum: pagination.current,
      pageSize: pagination.pageSize,
      name: nameFilter || '',
      batchId: batchIdFilter,
    })
  }

  // Validate that end date is not before start date
  const validateEndDate = (_: any, value: any) => {
    const startDate = form.getFieldValue('startTime')
    if (startDate && value && value.isBefore(startDate)) {
      return Promise.reject('报名结束时间不能早于开始时间')
    }
    return Promise.resolve()
  }

  const columns: Array<ColumnType<CourseData>> = [
    {
      title: '课程 ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '课程名称',
      dataIndex: 'name',
      key: 'name',
      filteredValue: queryParams.name ? [queryParams.name] : [],
      filterDropdown: (props: any) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="搜索课程名称"
              value={selectedKeys[0] as string}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => {
                confirm?.()
              }}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="primary"
                onClick={() => {
                  confirm?.()
                }}
                size="small"
                style={{ width: 90 }}
              >
                搜索
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.()
                }}
                size="small"
                style={{ width: 50 }}
              >
                重置
              </Button>
            </div>
          </div>
        )
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: CourseData) =>
        record.name.toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: '所属考试项目',
      dataIndex: 'batchName',
      key: 'batchName',
      filters: Array.isArray(batches)
        ? batches.map((b) => ({ text: b.name, value: b.id }))
        : [],
      filteredValue: queryParams.batchId ? [queryParams.batchId] : [],
      filterDropdown: (props: any) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props
        return (
          <div style={{ padding: 8 }}>
            <Select
              placeholder="选择考试项目"
              value={selectedKeys[0] as number}
              onChange={(value) => setSelectedKeys(value ? [value] : [])}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
              allowClear
              options={
                Array.isArray(batches)
                  ? batches.map((b) => ({
                      value: b.id,
                      label: b.name,
                    }))
                  : []
              }
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="primary"
                onClick={() => {
                  confirm?.()
                  console.log('Selected batchId:', selectedKeys[0])
                }}
                size="small"
                style={{ width: 90 }}
              >
                确定
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.()
                }}
                size="small"
                style={{ width: 50 }}
              >
                重置
              </Button>
            </div>
          </div>
        )
      },
      onFilter: (value: any, record: CourseData) => {
        if (!Array.isArray(batches)) return false
        const batch = batches.find((b) => b.id === value)
        return batch ? record.batchName === batch.name : false
      },
      render: (batchName: string) => <Link to="/batch">{batchName}</Link>,
    },
    {
      title: '报名开始',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: CourseData, b: CourseData) =>
        dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf(),
    },
    {
      title: '报名结束',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: CourseData, b: CourseData) =>
        dayjs(a.endTime).valueOf() - dayjs(b.endTime).valueOf(),
    },
    {
      title: '班级数量',
      key: 'classNum',
      dataIndex: 'classNum',
      render: (classNum: number | null, record: CourseData) => (
        <Link to="/class" search={{ courseId: record.id }}>
          {classNum ?? 0}
        </Link>
      ),
    },
    {
      title: '总容量',
      key: 'totalNum',
      dataIndex: 'totalNum',
      render: (totalNum: number | null) => totalNum ?? 0,
    },
    {
      title: '已报名人数',
      key: 'enrollNum',
      dataIndex: 'enrollNum',
      render: (enrollNum: number | null) => enrollNum ?? 0,
    },
    {
      title: '待支付',
      key: 'pendingPaymentCount',
      dataIndex: 'pendingPaymentCount',
      render: (count: number | undefined) => count ?? 0,
    },
    {
      title: '待签约',
      key: 'pendingContractCount',
      dataIndex: 'pendingContractCount',
      render: (count: number | undefined) => count ?? 0,
    },
    {
      title: '已完成',
      key: 'classAllocatedCount',
      dataIndex: 'classAllocatedCount',
      render: (count: number | undefined, record: CourseData) =>
        (count ?? 0) + (record.refundedCount ?? 0),
    },
    {
      title: '排序序号',
      key: 'sort',
      dataIndex: 'sort',
      sorter: (a: CourseData, b: CourseData) => (a.sort || 0) - (b.sort || 0),
    },
    {
      title: '剩余名额',
      key: 'remainingCapacity',
      render: (_: any, record: CourseData) => {
        const totalCapacity = record.totalNum ?? 0
        const enrolled = record.enrollNum ?? 0
        const remaining = totalCapacity - enrolled
        if (remaining <= 0) {
          return <span style={{ color: '#ff4d4f', fontWeight: 600 }}>已售罄</span>
        }
        const color =
          remaining <= 5 ? '#ff4d4f' : remaining <= 10 ? '#faad14' : undefined
        return (
          <span style={{ color, fontWeight: 600 }}>{remaining}</span>
        )
      },
    },
    {
      title: '容量进度',
      key: 'capacityProgress',
      render: (_: any, record: CourseData) => {
        const totalCapacity = record.totalNum ?? 0
        if (totalCapacity === 0) return '-'
        const enrolled = record.enrollNum ?? 0
        const remaining = totalCapacity - enrolled
        const percent = Math.round((enrolled / totalCapacity) * 100)
        let strokeColor = '#52c41a'
        if (remaining <= 0) strokeColor = '#ff4d4f'
        else if (remaining <= 5) strokeColor = '#ff4d4f'
        else if (remaining <= 10) strokeColor = '#faad14'
        return <Progress percent={percent} strokeColor={strokeColor} size="small" />
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CourseData) => (
        <Space size="middle">
          <Button size="small" onClick={() => showEditModal(record)}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const showAddModal = () => {
    setCurrentCourse(null)
    setFileList([])
    form.resetFields()
    setIsModalOpen(true)
  }

  const showEditModal = (course: CourseData) => {
    setCurrentCourse(course)

    // 如果有封面图片，转换为文件列表格式以便预览
    const coverFileList = course.cover
      ? [
          {
            uid: '-1',
            name: '封面图片',
            status: 'done' as const,
            url: course.cover,
          },
        ]
      : []

    setFileList(coverFileList)

    form.setFieldsValue({
      name: course.name,
      introduction: course.introduction,
      startTime: dayjs(course.startTime),
      endTime: dayjs(course.endTime),
      zhuangyuanId: course.zhuangyuanId,
      nonZhuangyuanId: course.nonZhuangyuanId,
      nonAgreementId: course.nonAgreementId,
      batchId: Array.isArray(batches)
        ? batches.find((b) => b.name === course.batchName)?.id
        : undefined,
      cover: coverFileList,
      sort: course.sort,
    })
    setIsModalOpen(true)
  }

  const handleOk = () => {
    form.validateFields().then((values) => {
      // 处理封面图片
      let coverFile = null
      if (values.cover && values.cover.length > 0) {
        const fileItem = values.cover[0]
        // 如果是新上传的文件，使用 originFileObj
        if (fileItem.originFileObj) {
          coverFile = fileItem.originFileObj
        }
        // 如果是已存在的文件（编辑时），不需要重新上传
      }

      const courseData = {
        ...values,
        name: values.name?.trim(),
        startTime: values.startTime.format('YYYY-MM-DDTHH:mm:ss'),
        endTime: values.endTime.format('YYYY-MM-DDTHH:mm:ss'),
        cover: coverFile,
      }

      if (currentCourse) {
        // 编辑
        updateCourseMutation.mutate({
          courseId: currentCourse.id,
          ...courseData,
        })
      } else {
        // 新增
        createCourseMutation.mutate(courseData)
      }
      setIsModalOpen(false)
    })
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该课程吗？',
      onOk: () => {
        deleteCourseMutation.mutate(id)
      },
    })
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">课程管理</h2>
        <Space>
          <Checkbox
            checked={showHistoryCourses}
            onChange={(e) => setShowHistoryCourses(e.target.checked)}
          >
            显示历史课程
          </Checkbox>
          <Button type="primary" onClick={showAddModal}>
            新增课程
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={courses}
        rowKey="id"
        bordered
        loading={isLoading}
        pagination={getPagination(
          queryParams.pageNum ?? 1,
          queryParams.pageSize ?? 10,
          totalCourses,
        )}
        onChange={handleTableChange}
      />

      <Modal
        title={currentCourse ? '编辑课程' : '新增课程'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        afterClose={() => {
          // Ensure body scroll is restored after modal closes
          restoreBodyScroll()
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="课程名称"
            rules={[
              { required: true, message: '请输入课程名称' },
              {
                validator: (_, value) => {
                  if (typeof value === 'string' && value.trim().length > 20) {
                    return Promise.reject(new Error('课程名称不能超过20个字'))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="cover"
            label="封面图片"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              setFileList(e?.fileList || [])
              return e?.fileList
            }}
          >
            <Upload {...uploadProps}>
              {fileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传封面</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item
            name="introduction"
            label={
              <Space>
                课程描述
                <Popover
                  content="左侧为 Markdown 编辑区，右侧为实时预览区。支持标准 Markdown 语法，点击工具栏可快速插入格式。"
                  placement="top"
                >
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Popover>
              </Space>
            }
          >
            <MDEditor
              value={form.getFieldValue('introduction') || ''}
              onChange={(value) =>
                form.setFieldsValue({ introduction: value || '' })
              }
              data-color-mode="light"
              commands={[
                ...getCommands(),
                {
                  name: 'image-upload',
                  keyCommand: 'image-upload',
                  buttonProps: { 'aria-label': 'Upload image' },
                  icon: (
                    <svg width="12" height="12" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"
                      />
                    </svg>
                  ),
                  execute: async () => {
                    const input = document.createElement('input')
                    input.setAttribute('type', 'file')
                    input.setAttribute('accept', 'image/*')

                    return new Promise((resolve) => {
                      input.onchange = async () => {
                        const file = input.files?.[0]
                        if (file) {
                          try {
                            const response =
                              await quillImageUploadMutation.mutateAsync(file)
                            if (response.data.imageUrl) {
                              const currentValue =
                                form.getFieldValue('introduction') || ''
                              const imageMarkdown = `![Image](${response.data.imageUrl})`
                              const newValue =
                                currentValue + '\n\n' + imageMarkdown + '\n\n'
                              form.setFieldsValue({ introduction: newValue })
                            }
                          } catch (error) {
                            console.error('Failed to upload image:', error)
                          }
                        }
                        input.remove()
                        resolve(undefined)
                      }
                      input.click()
                    })
                  },
                },
              ]}
            />
          </Form.Item>
          {/* 报名时间绑定到课程 */}
          <Form.Item
            name="startTime"
            label="报名开始时间"
            rules={[{ required: true, message: '请选择报名开始时间' }]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              className="w-full"
            />
          </Form.Item>
          <Form.Item
            name="endTime"
            label="报名结束时间"
            rules={[
              { required: true, message: '请选择报名结束时间' },
              { validator: validateEndDate },
            ]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              className="w-full"
            />
          </Form.Item>

          <Form.Item
            name="batchId"
            label="所属考试项目"
            rules={[{ required: true, message: '请选择所属考试项目' }]}
          >
            <Select
              placeholder="请选择所属考试项目"
              options={
                Array.isArray(batches)
                  ? batches.map((b) => ({
                      value: b.id,
                      label: b.name,
                    }))
                  : []
              }
            />
          </Form.Item>

          <Card title="协议绑定" className="mb-4">
            <Form.Item name="zhuangyuanId" label="状元协议">
              <Select
                placeholder="请选择状元协议"
                allowClear
                options={
                  Array.isArray(agreementTemplates)
                    ? agreementTemplates
                        .filter(
                          (t) => t.type === AgreementTemplateType.ZHUANGYUAN,
                        )
                        .map((t) => ({ value: t.id, label: t.content }))
                    : []
                }
              />
            </Form.Item>
            <Form.Item name="nonZhuangyuanId" label="非状元协议">
              <Select
                placeholder="请选择非状元协议"
                allowClear
                options={
                  Array.isArray(agreementTemplates)
                    ? agreementTemplates
                        .filter(
                          (t) => t.type === AgreementTemplateType.NONZHUANGYUAN,
                        )
                        .map((t) => ({ value: t.id, label: t.content }))
                    : []
                }
              />
            </Form.Item>
            <Form.Item name="nonAgreementId" label="非协议">
              <Select
                placeholder="请选择非协议"
                allowClear
                options={
                  Array.isArray(agreementTemplates)
                    ? agreementTemplates
                        .filter(
                          (t) => t.type === AgreementTemplateType.NONAGREEMENT,
                        )
                        .map((t) => ({ value: t.id, label: t.content }))
                    : []
                }
              />
            </Form.Item>
          </Card>

          <Form.Item
            name="sort"
            label={
              <Space>
                排序序号
                <Popover
                  content="请输入排序序号，数字越小排序越靠前"
                  placement="top"
                >
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Popover>
              </Space>
            }
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
